<script>
import debounce from 'lodash/debounce';
import map from 'lodash/map';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Layout from '@layouts/main';
import LeaveModel from '@models/Leave';
import { DateTime } from 'luxon';
import Multiselect from 'vue-multiselect';
import HeaderComponent from '@components/HeaderComponent';
import LeaveModal from '@views/leaves/edit-leave';
import LeaveCalendar from '@views/leaves/calendar';

export default {
  page() {
    return {
      title: this.$t('titles.leaves'),
    };
  },
  components: {
    Layout,
    FontAwesomeIcon,
    HeaderComponent,
    LeaveModal,
    LeaveCalendar,
    Multiselect,
  },
  props: {
    locations: {
      required: true,
      type: Array,
      default: null,
    },
    areas: {
      required: true,
      type: Array,
      default: null,
    },
  },
  data() {
    return {
      sortBy: 'start_date',
      sortDesc: true,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      selectedLeave: null,
      fieldsTable: [
        {
          key: 'start_date',
          label: this.$t('table_labels.start_date'),
          sortable: true,
          formatter: (value) => DateTime.fromSQL(value).toFormat('dd.MM.yyyy'),
        },
        {
          key: 'end_date',
          label: this.$t('table_labels.end_date'),
          sortable: true,
          formatter: (value) => DateTime.fromSQL(value).toFormat('dd.MM.yyyy'),
        },
        { key: 'driver', label: this.$t('table_labels.name'), sortable: true },
        { key: 'amount_of_days', label: this.$t('table_labels.amount_of_days'), sortable: true },
        { key: 'role', label: this.$t('table_labels.role'), sortable: false, thStyle: { width: '10%' } },
        { key: 'confirmed', label: this.$t('table_labels.confirmed'), sortable: true },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
      filters: {
        search: null,
        areasAndLocations: null,
      },
      areasAndLocations: [],
      focused: false,
      activeView: 'list',
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  watch: {
    filters: {
      handler() {
        this.$nextTick(() => {
          this.currentPage = 1;
          if (this.activeView === 'list') {
            this.$refs.asyncTable.refresh();
          } else if (this.$refs.leaveCalendar) {
            this.$refs.leaveCalendar.refetchEvents(); 
        }
        });
      },
      immediate: false,
      deep: true,
    },
  },  
  mounted() {
    this.areasAndLocations = [
      {
        type: this.$t('fields.areas'),
        options: this.areas,
      },
      {
        type: this.$t('fields.locations'),
        options: this.locations,
      },
    ];
  },
  methods: {
    itemsProvider(ctx) {
      return LeaveModel.orderBy(this.sortDir + ctx.sortBy)
        .params({
          areas_and_locations: map(this.filters.areasAndLocations, 'id'),
        })
        .where('search', this.filters.search)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    exportPdf(id) {
      this.$wait.start(`downloading${id}`);
      this.$http
        .get(`leaves/export/${id}`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading${id}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          let filename = `Leave#${id}`;
          const match = response.headers['content-disposition'].match(/filename="([^"]+)"/);

          if (match && match.length > 1) {
            filename = match[1];
          }

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading${id}`));
    },
    confirm(id) {
      if (confirm(this.$t('messages.confirm_leave'))) {
        this.$wait.start(`confirm ${id}`);
        new LeaveModel({ id: `confirm/${id}` })
          .save()
          .then(() => {
            this.$refs.asyncTable.refresh();
            this.$wait.end(`confirm ${id}`);
          })
          .catch(() => {
            this.$wait.end(`confirm ${id}`);
          });
      }
    },
    updateFilter: debounce(function (value) {
      this.filters.search = value;
    }, 500),
    customLocationName({ name }) {
      return name;
    },
    editLeave(leave) {
      this.selectedLeave = leave;
    },
    onSubmit() {
      this.selectedLeave = null;
      if (this.activeView === 'list') {
        this.$refs.asyncTable.refresh();
      } else {
        this.$refs.leaveCalendar.refetchEvents();
      }
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.leave') }))) {
        this.$wait.start('leave processing');
        const leave = new LeaveModel({ id });
        await leave.delete();
        this.$wait.end('leave processing');
        this.$refs.asyncTable.refresh();
      }
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <HeaderComponent :title="$t('titles.leave')" :button-text="$t('titles.create_leave')" @button-pressed="selectedLeave = 'new'"></HeaderComponent>
      </b-col>
    </b-row>
    
    <b-row class="mb-4">
      <b-col sm="12" md="4" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('titles.view') }}</span>
          </template>
        </HeaderComponent>
        <b-form-group>
          <b-form-radio-group
            id="radio-group-active-view"
            v-model="activeView"
            buttons
            size="sm"
            name="radio-sub-component"
            button-variant="outline-secondary border-gray"
            class="w-100"
          >
            <b-form-radio class="border-muted" value="list">{{ $t('actions.list') }}</b-form-radio>
            <b-form-radio class="border-muted" value="calendar">{{ $t('actions.calendar') }}</b-form-radio>
          </b-form-radio-group>
        </b-form-group>
      </b-col>
      <b-col sm="12" md="12" lg="4" class="mb-3">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.search') }}</span
            >&nbsp;
            <small class="pt-01">
              {{
                $t('search.message', {
                  entity: $t('search.entities.name'),
                })
              }}
            </small>
          </template>
        </HeaderComponent>
        <b-input-group>
          <b-form-input :value="filters.search" class="form-control-sm" :placeholder="$t('placeholders.search')" @input="updateFilter"></b-form-input>
          <b-input-group-append>
            <b-btn variant="secondary" size="sm" class="text-white" :disabled="!filters.search" @click="filters.search = ''">{{ $t('actions.clear') }}</b-btn>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col sm="12" md="12" lg="4">
        <HeaderComponent>
          <template v-slot:header>
            <span>{{ $t('fields.areas_and_locations') }}</span>
          </template>
        </HeaderComponent>
        <b-form-group>
          <b-input-group>
            <div class="multiselect-container kks-multiselect-container-index form-control form-control-sm p-0" :class="{ focused: focused }">
              <Multiselect
                v-model="filters.areasAndLocations"
                :options="areasAndLocations"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :placeholder="$t('placeholders.areas_and_locations_multiple')"
                :label="$t('fields.locations')"
                :custom-label="customLocationName"
                track-by="id"
                group-values="options"
                group-label="type"
                @open="focused = true"
                @close="focused = false"
                >&nbsp;</Multiselect
              >
            </div>
            <b-input-group-append>
              <b-btn
                variant="secondary"
                size="sm"
                class="text-white border-gray"
                :disabled="!filters.areasAndLocations"
                @click="filters.areasAndLocations = []"
                >{{ $t('actions.clear') }}</b-btn
              >
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>
    <template v-if="activeView === 'list'">
      <div id="leaves-index" class="position-relative">
        <b-table
          ref="asyncTable"
          small
          striped
          :items="itemsProvider"
          stacked="md"
          :busy.sync="isBusy"
          :current-page="currentPage"
          :per-page="perPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :fields="fieldsTable"
          :no-sort-reset="true"
          :show-empty="true"
        >
          <!-- custom sorting icons -->
          <template #head()="{ label, field: { key, sortable } }">
            {{ label }}
            <template v-if="sortable">
              <!-- Custom icons -->
              <template>
                <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
                <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
                <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
              </template>
            </template>
          </template>
          <template v-slot:cell(confirmed)="row">
            <template v-if="row.item.confirmed">
              <FontAwesomeIcon :icon="['fas', 'check']" class="text-success mr-2" />
              <span v-if="row.item.confirmed_by && row.item.confirmed_at" class="near-label-info text-secondary" @click.stop>
                <FontAwesomeIcon :id="`confirmPopover${row.item.id}`" :icon="['fas', 'info-circle']" tabindex="-1" />
                <b-popover :target="`confirmPopover${row.item.id}`" triggers="focus" placement="rightbottom">
                  {{ $t('infos.leave_confirmed_by', { name: row.item.confirmed_by.name, date: row.item.confirmed_at }) }}
                </b-popover>
              </span>
            </template>
            <template v-else>
              <FontAwesomeIcon :icon="['fas', 'times-circle']" class="text-danger" />
            </template>
          </template>
          <template v-slot:cell(role)="row">
            <b-badge v-if="row.item.driver_roles && row.item.driver_roles.includes('admin')" variant="secondary">{{ $t('fields.admin') }}</b-badge>
            <b-badge v-else variant="info">{{ $t('fields.driver') }}</b-badge>
          </template>
          <template v-slot:cell(actions)="row">
            <b-btn
              :disabled="row.item.confirmed == true"
              class="mr-2"
              size="sm"
              :variant="row.item.confirmed == true ? `outline-success` : `success`"
              @click="confirm(row.item.id)"
            >
              <FontAwesomeIcon :icon="['fas', 'check']" />
            </b-btn>
            <b-btn class="mr-2" variant="outline-primary" size="sm" :disabled="$wait.is(`downloading${row.item.id}`)" @click="exportPdf(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'file-export']" />
              <FontAwesomeIcon v-if="$wait.is(`downloading${row.item.id}`)" class="ml-1" icon="spinner" spin />
            </b-btn>
            <b-btn class="mr-2" size="sm" variant="outline-primary" @click="editLeave(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'edit']" />
            </b-btn>
            <b-btn size="sm" variant="outline-danger" @click.stop="deleteEntity(row.item.id)">
              <FontAwesomeIcon :icon="['fas', 'times-circle']" />
            </b-btn>
          </template>
          <template v-slot:cell(driver)="row">
            <span v-if="row.item.driver"> {{ row.item.driver.name }} </span>
          </template>
          <template v-slot:cell(empty)="row">
            <div class="text-center">{{ $t('messages.no_items') }}</div>
          </template>
        </b-table>
      </div>
      <b-row>
        <b-col sm="12">
          <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
        </b-col>
      </b-row>
    </template>
    <LeaveCalendar v-else ref="leaveCalendar" :search="filters.search" :areas-and-locations="filters.areasAndLocations" @open-leave="editLeave" />
    <LeaveModal v-model="selectedLeave" :loaded-id="selectedLeave" @updated="onSubmit" @hidden="selectedLeave = null" />
  </Layout>
</template>
